import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Users, CheckCircle, Circle, Clock, ChevronDown, ChevronUp, Save, Shield, Key, Briefcase } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StaffOnboardingPage = () => {
  const { user } = useAuth();
  const [staffList, setStaffList] = useState([]);
  const [checklistTemplate, setChecklistTemplate] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedUser, setExpandedUser] = useState(null);
  const [userDetails, setUserDetails] = useState({});
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchStaffList();
  }, [statusFilter]);

  const fetchStaffList = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams();
      if (statusFilter) params.append('status_filter', statusFilter);

      const response = await fetch(`${API_URL}/api/staff-admin/onboarding?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch staff list');

      const data = await response.json();
      setStaffList(data.staff || []);
      setChecklistTemplate(data.checklist_template || []);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/staff-admin/onboarding/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch details');

      const data = await response.json();
      setUserDetails(prev => ({ ...prev, [userId]: data }));
    } catch (err) {
      console.error('Failed to fetch user details:', err);
    }
  };

  const handleToggleExpand = async (userId) => {
    if (expandedUser === userId) {
      setExpandedUser(null);
    } else {
      setExpandedUser(userId);
      if (!userDetails[userId]) {
        await fetchUserDetails(userId);
      }
    }
  };

  const handleChecklistUpdate = async (userId, itemKey, completed, notes) => {
    setSaving(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/staff-admin/onboarding/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: [{ item_key: itemKey, completed, notes }]
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to update');

      // Refresh data
      await fetchUserDetails(userId);
      await fetchStaffList();
      
      setMessage({ type: 'success', text: 'Checklist updated' });
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const getProgressColor = (progress) => {
    if (progress.is_complete) return 'text-green-600 bg-green-100';
    if (progress.required_completed / progress.required_total >= 0.5) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'super_admin': return <Shield className="w-4 h-4 text-purple-600" />;
      case 'admin': return <Key className="w-4 h-4 text-blue-600" />;
      default: return <Briefcase className="w-4 h-4 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="staff-onboarding-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Users className="w-7 h-7 text-blue-600" />
              Staff Onboarding
            </h1>
            <p className="text-gray-600 mt-1">
              Track readiness and onboarding status of internal staff accounts.
            </p>
            <p className="text-xs text-gray-400 mt-1" data-testid="staff-onboarding-applicability">
              These checklists apply to <span className="font-medium">staff you onboard</span>. Founder / Super-Admin accounts are not required to complete new-hire onboarding items — an incomplete count here does not reflect Founder account status.
            </p>
          </div>

          <div className="flex items-center gap-4">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              data-testid="status-filter"
            >
              <option value="">All Staff</option>
              <option value="complete">Onboarding Complete</option>
              <option value="incomplete">Onboarding Incomplete</option>
            </select>
          </div>
        </div>

        {/* Message */}
        {message && (
          <div className={`p-3 rounded-lg text-sm ${
            message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}>
            {message.text}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="text-2xl font-bold text-blue-600">{staffList.length}</div>
            <div className="text-sm text-gray-600">Total Staff</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="text-2xl font-bold text-green-600">
              {staffList.filter(s => s.onboarding_progress?.is_complete).length}
            </div>
            <div className="text-sm text-gray-600">Onboarding Complete</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="text-2xl font-bold text-yellow-600">
              {staffList.filter(s => !s.onboarding_progress?.is_complete).length}
            </div>
            <div className="text-sm text-gray-600">In Progress</div>
          </div>
        </div>

        {/* Staff List */}
        <div className="space-y-4">
          {staffList.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
              No staff members found.
            </div>
          ) : (
            staffList.map((staff) => (
              <div key={staff.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                {/* Staff Header Row */}
                <div
                  className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => handleToggleExpand(staff.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        {getRoleIcon(staff.role)}
                        <div>
                          <div className="font-medium text-gray-900">
                            {staff.first_name} {staff.last_name}
                          </div>
                          <div className="text-sm text-gray-500">{staff.email}</div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      {/* Progress Badge */}
                      <div className={`px-3 py-1 rounded-full text-sm font-medium ${getProgressColor(staff.onboarding_progress)}`}>
                        {staff.onboarding_progress?.total_completed}/{staff.onboarding_progress?.total_items} Complete
                      </div>

                      {/* Status Icon */}
                      {staff.onboarding_progress?.is_complete ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <Clock className="w-5 h-5 text-yellow-600" />
                      )}

                      {/* Expand Icon */}
                      {expandedUser === staff.id ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {/* Role & Created */}
                  <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                    <span className="capitalize">{staff.role?.replace(/_/g, ' ')}</span>
                    <span>•</span>
                    <span>Created {new Date(staff.created_at).toLocaleDateString()}</span>
                    {staff.mfa_enabled && (
                      <>
                        <span>•</span>
                        <span className="text-green-600 flex items-center gap-1">
                          <Shield className="w-3 h-3" /> MFA Enabled
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {/* Expanded Checklist */}
                {expandedUser === staff.id && userDetails[staff.id] && (
                  <div className="border-t border-gray-200 p-4 bg-gray-50">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Onboarding Checklist</h4>
                    <div className="space-y-3">
                      {userDetails[staff.id].checklist?.items?.map((item) => (
                        <div key={item.key} className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-200">
                          <button
                            onClick={() => handleChecklistUpdate(staff.id, item.key, !item.completed, item.notes)}
                            disabled={saving}
                            className="mt-0.5"
                          >
                            {item.completed ? (
                              <CheckCircle className="w-5 h-5 text-green-600" />
                            ) : (
                              <Circle className="w-5 h-5 text-gray-300 hover:text-gray-400" />
                            )}
                          </button>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className={`text-sm ${item.completed ? 'text-gray-600' : 'text-gray-900 font-medium'}`}>
                                {item.label}
                              </span>
                              {item.required && (
                                <span className="text-xs text-red-500">*Required</span>
                              )}
                            </div>
                            {item.notes && (
                              <p className="text-xs text-gray-500 mt-1">{item.notes}</p>
                            )}
                            {item.completed_at && (
                              <p className="text-xs text-gray-400 mt-1">
                                Completed {new Date(item.completed_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Progress Summary */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">
                          Required items: {userDetails[staff.id].checklist?.progress?.required_completed} / {userDetails[staff.id].checklist?.progress?.required_total}
                        </span>
                        {userDetails[staff.id].checklist?.completed_at && (
                          <span className="text-green-600 font-medium inline-flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" /> Onboarding completed {new Date(userDetails[staff.id].checklist.completed_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default StaffOnboardingPage;
